// ── Filtered process list (rows + empty state) ─────────────────────────────────
import type { ReactNode } from 'react'
import { Text, YStack } from 'tamagui'

import { useApp } from '../../../i18n'
import { Card, Enter } from '../../../ui'
import type { ProcStatus } from '../registry'
import type { ProcVM } from '../processVM'
import { ProcessRow } from './ProcessRow'

/** Статус, выбранный чипами StatusChips; 'all' — всё, кроме отменённых (они — в чипе «Архив»). */
export type Filter = 'all' | ProcStatus

/** Отменённые с глаз долой: «Все» их не показывает и не считает (просьба аналитика 05.11.2026). */
export const inFilter = (p: Pick<ProcVM, 'status'>, filter: Filter) =>
  filter === 'all' ? p.status !== 'cancelled' : p.status === filter

/**
 * The body of the Processes list: a VM `source` (already narrowed by tab/search/type on
 * /processes) filtered by the StatusChips selection (owned by the parent, since the chips
 * and the list are siblings). Reused verbatim by the Home card.
 */
export function ProcessListWithFilters({
  source,
  onOpen,
  filter,
  cascadeKey = '',
  limit,
  empty,
}: {
  source: ProcVM[]
  onOpen: (p: ProcVM) => void
  /** Status picked via StatusChips; 'all' shows everything but cancelled (the «Архив» chip). */
  filter: Filter
  /** Extra key segment (e.g. the active tab) — changing it re-runs the row cascade. */
  cascadeKey?: string
  /** Сколько строк показывать (после фильтра). Home режет до 10 — «Показать все» ведёт на /processes. */
  limit?: number
  /** Своё пустое состояние (на /processes — «Ничего не найдено» со сбросом фильтров). */
  empty?: ReactNode
}) {
  const { l } = useApp()
  const list = source
    .filter((p) => inFilter(p, filter))
    .slice(0, limit)
  return list.length ? (
    <YStack gap={12}>
      {list.map((p, i) => (
        // key includes tab + status filter so switching either re-runs the cascade
        <Enter key={`${cascadeKey}-${filter}-${p.id}`} index={Math.min(i, 11)}>
          <ProcessRow p={p} onOpen={onOpen} />
        </Enter>
      ))}
    </YStack>
  ) : (
    empty ?? (
      <Card padding={48} alignItems="center">
        <Text fontSize={13} color="$muted">
          {l('doc.empty')}
        </Text>
      </Card>
    )
  )
}
